import { apiFetch } from "@/lib/api";

import { buildConfigurationSubmitSupportContext } from "./configurationSupportLogContext";

import type { BatchTeamNotesPreview } from "@/components/manufacturing/BatchTeamNotesPanel";

export type ConfigurationQueueLineKind = "batch_line" | "alternate_order";

export interface ConfigurationQueueItem {
  kind: ConfigurationQueueLineKind;
  batchItemId: number | null;
  alternateOrderId: number | null;
  batchId: number | null;
  sku: string;
  supported: boolean;
  totalQuantity: number;
  quantityAssembled: number;
  remainingToBuild: number;
  buildOrderNumber: number | null;
  buildOrderId: number | null;
  isRebuildOrder: boolean;
  salesOrderId: number | null;
  salesOrderNumber: string | null;
  mfrShipByDate: string | null;
  requiresEncapsulation: boolean | null;
  teamNoteCount: number;
  latestTeamNote: BatchTeamNotesPreview | null;
  requiredTlaSku: string | null;
  inventoryAlternatesOnHand: number;
}

export interface ConfigurationQueueResponse {
  locationId: number;
  generatedAt: string;
  items: ConfigurationQueueItem[];
}

export interface ConfigurationAlternatesResponse {
  sku: string;
  deviceLabelSerials: string[];
}

export interface ConfigurationRequiredTlaResponse {
  finishedSku: string;
  variableName: string;
  tlaSku: string | null;
  quantity: number | null;
}

export interface SubmitConfigurationBody {
  finishedSku: string;
  firmware: string;
  mcuUuid: string | null;
  pcbaSerial: string | null;
  clampSerial: string | null;
  deviceLabelSerial: string;
  childPcba: string | null;
  imei: string | null;
  iccid: string | null;
  batchItemId: number | null;
  buildOrderNumber: number | null;
  isAlternate: boolean;
  alternateOrderId?: number | null;
  rescanSerialFciId?: number | null;
}

export interface SubmitConfigurationResult {
  ok: true;
  mode: "assemble" | "rebuild" | "rescan";
  serialFciId: number;
  archivedSerialFciId: number | null;
  deviceLabelSerial: string;
  finishedSku: string;
  wirelessPortal: "none" | "added" | "updated";
  message: string;
}

/** Operator-facing submit success copy for the configure button (no serial_fci ids). */
export function configurationSubmitSuccessLabel(result: SubmitConfigurationResult): string {
  if (result.mode === "rescan") {
    return `Updated serials for ${result.deviceLabelSerial}`;
  }
  const verb = result.mode === "rebuild" ? "Rebuilt" : "Configured";
  return `${verb} ${result.finishedSku} ${result.deviceLabelSerial}`;
}

export interface ConfigurationBuiltUnit {
  serialFciId: number;
  deviceLabelSerial: string;
  pcbaSerial: string | null;
  clampSerial: string | null;
  mcuUuid: string | null;
  imei: string | null;
  iccid: string | null;
  dateAssembled: string | null;
}

export interface ConfigurationBuiltUnitsResponse {
  units: ConfigurationBuiltUnit[];
}

export interface ConfigurationJobProgress {
  totalQuantity: number;
  quantityAssembled: number;
  remainingToBuild: number;
  generatedAt: string;
}

export const CONFIGURATION_QUEUE_QUERY_KEY = ["manufacturing", "configuration", "sgs-queue"] as const;

/** Multi-operator job-progress / built-units sync while units remain on the job. */
export const CONFIGURATION_JOB_SYNC_INTERVAL_MS = 5_000;
/** Solo slow poll at zero remaining so QC removals can reopen the job. */
export const CONFIGURATION_JOB_SYNC_AT_ZERO_INTERVAL_MS = 10_000;
/** Open labor sessions on Configuration jobs (gates heavy sync polling). */
export const CONFIGURATION_JOB_PRESENCE_INTERVAL_MS = 15_000;

export const configurationActiveJobOperatorsQueryKey = [
  "manufacturing",
  "configuration",
  "labor",
  "active-job-operators",
] as const;

export interface ConfigurationJobOperator {
  userId: string;
  batchItemId: number | null;
  alternateOrderId: number | null;
  alternateSku: string | null;
  email: string;
  fullName: string | null;
  avatarUrl: string | null;
}

export async function fetchConfigurationActiveJobOperators(): Promise<ConfigurationJobOperator[]> {
  const data = await apiFetch<{ operators: ConfigurationJobOperator[] }>(
    "/api/labor/config/active-job-operators",
    { cache: "no-store" },
  );
  return data.operators;
}

export function fetchConfigurationQueue(): Promise<ConfigurationQueueResponse> {
  return apiFetch<ConfigurationQueueResponse>("/api/manufacturing/configuration/sgs-queue");
}

export function fetchConfigurationAlternates(sku: string): Promise<ConfigurationAlternatesResponse> {
  return apiFetch<ConfigurationAlternatesResponse>("/api/manufacturing/configuration/alternates", {
    query: { sku },
  });
}

export interface ConfigurationPcbaPrefixTlaCheckResponse {
  pcba_serial: string;
  pcba_prefix: string | null;
  required_tla_sku: string | null;
  expectation: {
    pcba_prefix: string;
    core_feature: string;
    expected_tla_sku: string;
    tla_build_order_id: number | null;
    tla_work_order_number: string | null;
    source: "planned" | "serial_exception";
    notes: string | null;
    allowed_tla_skus: string[] | null;
  } | null;
  matches_required_tla: boolean | null;
  mismatch_message: string | null;
  has_prefix_assignment: boolean | null;
  tla_assembly_output_sku: string | null;
}

export interface ConfigurationClampQcCheckResponse {
  clamp_serial: string;
  skipped: boolean;
  eligible: boolean;
  block_message: string | null;
}

export interface ConfigurationOpenQualityIncidentCheckResponse {
  skipped: boolean;
  eligible: boolean;
  block_message: string | null;
  incident_id: number | null;
}

export interface ConfigurationPcbaTestRowSummary {
  id: number;
  power_test_pass: boolean | null;
  firmware_pass: boolean | null;
  ban: boolean;
  power_test_timestamp: string | null;
  firmware_timestamp: string | null;
  firmware_version: string | null;
  firmware_file: string | null;
  is_latest: boolean;
}

export interface ConfigurationPcbaTestCheckResponse {
  mcu_uuid: string;
  finished_sku: string;
  skipped: boolean;
  eligible: boolean;
  missing_row: boolean;
  block_message: string | null;
  pcba_test_id: number | null;
  tests: ConfigurationPcbaTestRowSummary[];
}

export function fetchConfigurationPcbaPrefixTlaCheck(params: {
  pcba_serial: string;
  finished_sku?: string;
  required_tla_sku?: string;
}): Promise<ConfigurationPcbaPrefixTlaCheckResponse> {
  return apiFetch<ConfigurationPcbaPrefixTlaCheckResponse>(
    "/api/manufacturing/configuration/pcba-prefix-tla-check",
    { query: params },
  );
}

export function fetchConfigurationClampQcCheck(
  clampSerial: string,
): Promise<ConfigurationClampQcCheckResponse> {
  return apiFetch<ConfigurationClampQcCheckResponse>(
    "/api/manufacturing/configuration/clamp-qc-check",
    { query: { clamp_serial: clampSerial } },
  );
}

export function fetchConfigurationOpenQualityIncidentCheck(params: {
  pcbaSerial?: string | null;
  clampSerial?: string | null;
  deviceLabelSerial?: string | null;
  serialFciId?: number | null;
}): Promise<ConfigurationOpenQualityIncidentCheckResponse> {
  return apiFetch<ConfigurationOpenQualityIncidentCheckResponse>(
    "/api/manufacturing/configuration/open-qc-incident-check",
    {
      query: {
        ...(params.pcbaSerial != null && params.pcbaSerial.trim() !== ""
          ? { pcba_serial: params.pcbaSerial.trim() }
          : {}),
        ...(params.clampSerial != null && params.clampSerial.trim() !== ""
          ? { clamp_serial: params.clampSerial.trim() }
          : {}),
        ...(params.deviceLabelSerial != null && params.deviceLabelSerial.trim() !== ""
          ? { device_label_serial: params.deviceLabelSerial.trim() }
          : {}),
        ...(params.serialFciId != null && Number.isFinite(params.serialFciId) && params.serialFciId > 0
          ? { serial_fci_id: String(Math.trunc(params.serialFciId)) }
          : {}),
      },
    },
  );
}

export function fetchConfigurationPcbaTestCheck(params: {
  mcu_uuid: string;
  finished_sku: string;
  pcba_serial?: string | null;
}): Promise<ConfigurationPcbaTestCheckResponse> {
  return apiFetch<ConfigurationPcbaTestCheckResponse>(
    "/api/manufacturing/configuration/pcba-test-check",
    {
      query: {
        mcu_uuid: params.mcu_uuid,
        finished_sku: params.finished_sku,
        ...(params.pcba_serial != null && params.pcba_serial.trim() !== ""
          ? { pcba_serial: params.pcba_serial.trim() }
          : {}),
      },
    },
  );
}

export function fetchConfigurationRequiredTla(sku: string): Promise<ConfigurationRequiredTlaResponse> {
  return apiFetch<ConfigurationRequiredTlaResponse>("/api/manufacturing/configuration/required-tla", {
    query: { sku },
  });
}

export function submitConfiguration(body: SubmitConfigurationBody): Promise<SubmitConfigurationResult> {
  return apiFetch<SubmitConfigurationResult>("/api/manufacturing/configuration/submit", {
    method: "POST",
    body,
    supportLogContext: buildConfigurationSubmitSupportContext(body),
  });
}

export function fetchConfigurationBuiltUnits(params: {
  batchItemId?: number;
  alternateOrderId?: number;
}): Promise<ConfigurationBuiltUnitsResponse> {
  return apiFetch<ConfigurationBuiltUnitsResponse>("/api/manufacturing/configuration/built-units", {
    query: params,
  });
}

export function fetchConfigurationJobProgress(params: {
  batchItemId?: number;
  alternateOrderId?: number;
}): Promise<ConfigurationJobProgress> {
  return apiFetch<ConfigurationJobProgress>("/api/manufacturing/configuration/job-progress", {
    query: params,
  });
}

export function configurationQueueRowKey(item: ConfigurationQueueItem): string {
  if (item.kind === "alternate_order" && item.alternateOrderId != null) {
    return `alternate-order-${item.alternateOrderId}`;
  }
  return `batch-line-${item.batchItemId ?? 0}`;
}

export function compareConfigurationQueueItems(
  left: ConfigurationQueueItem,
  right: ConfigurationQueueItem,
): number {
  const leftDate = left.mfrShipByDate?.trim() ?? "";
  const rightDate = right.mfrShipByDate?.trim() ?? "";
  if (leftDate === "" && rightDate !== "") return 1;
  if (leftDate !== "" && rightDate === "") return -1;
  if (leftDate !== rightDate) return leftDate.localeCompare(rightDate);
  const leftKey = left.alternateOrderId ?? left.batchItemId ?? 0;
  const rightKey = right.alternateOrderId ?? right.batchItemId ?? 0;
  if (leftKey !== rightKey) return leftKey - rightKey;
  if (left.batchId !== right.batchId) return (left.batchId ?? 0) - (right.batchId ?? 0);
  return left.sku.localeCompare(right.sku);
}
